import "./dragdrop.scss";
import React from "react";
import { connect } from "react-redux";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { Select, type SelectOptionType } from "@onshopfront/components";
import { hideDialog } from "../../../Actions/Dialogs/DialogActions";
import { type ConnectedShopfront } from "../../../Utilities/Generic/ActionsHelper";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import Button from "../../Buttons/Button";
import InlineSelect from "../../InlineSelect/InlineSelect";
import ColorPicker from "../ColorPicker";

export type BorderStyleType =
    "none" |
    "hidden" |
    "dotted" |
    "dashed" |
    "solid" |
    "double" |
    "groove" |
    "ridge" |
    "inset" |
    "outset";

export type BorderValues = {
    borderWidth: string;
    borderStyle: BorderStyleType;
    borderColor: string;
};

export type BorderSettingsType = {
    top: BorderValues;
    left: BorderValues;
    right: BorderValues;
    bottom: BorderValues;
};

const connector = connect();

interface Props extends ConnectedShopfront<typeof connector> {
    border: BorderSettingsType;
    onConfirm: (border: BorderSettingsType) => void;
}

type State = {
    border: BorderSettingsType;
    side: BorderSideType;
    advanced: boolean;
};

type BorderSideType = "all" | "top" | "bottom" | "left" | "right";

export function isBorderAdvanced(border: BorderSettingsType): boolean {
    return (
        border.top.borderColor !== border.bottom.borderColor ||
        border.top.borderStyle !== border.bottom.borderStyle ||
        border.top.borderWidth !== border.bottom.borderWidth
    ) || (
        border.top.borderColor !== border.right.borderColor ||
        border.top.borderStyle !== border.right.borderStyle ||
        border.top.borderWidth !== border.right.borderWidth
    ) || (
        border.top.borderColor !== border.left.borderColor ||
        border.top.borderStyle !== border.left.borderStyle ||
        border.top.borderWidth !== border.left.borderWidth
    );
}

export function createBorderStyles(border: BorderSettingsType): React.CSSProperties {
    if(!border) {
        return {};
    }

    return {
        borderTopColor   : border.top.borderColor,
        borderTopStyle   : border.top.borderStyle,
        borderTopWidth   : `${border.top.borderWidth}px`,
        borderLeftColor  : border.left.borderColor,
        borderLeftStyle  : border.left.borderStyle,
        borderLeftWidth  : `${border.left.borderWidth}px`,
        borderRightColor : border.right.borderColor,
        borderRightStyle : border.right.borderStyle,
        borderRightWidth : `${border.right.borderWidth}px`,
        borderBottomColor: border.bottom.borderColor,
        borderBottomStyle: border.bottom.borderStyle,
        borderBottomWidth: `${border.bottom.borderWidth}px`,
    };
}

const BORDER_SELECTOR_MAP: Record<string, IconProp> = {
    top   : "caret-down",
    left  : "caret-right",
    right : "caret-left",
    bottom: "caret-up",
};

const BORDER_STYLE_OPTIONS: Array<SelectOptionType<BorderStyleType>> = [{
    label: "None",
    value: "none",
}, {
    label: "Hidden",
    value: "hidden",
}, {
    label: "Dotted",
    value: "dotted",
}, {
    label: "Dashed",
    value: "dashed",
}, {
    label: "Solid",
    value: "solid",
}, {
    label: "Double",
    value: "double",
}, {
    label: "Groove",
    value: "groove",
}, {
    label: "Ridge",
    value: "ridge",
}, {
    label: "Inset",
    value: "inset",
}, {
    label: "Outset",
    value: "outset",
}];

export class BorderSettingsDialog extends React.Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props);

        const advanced = isBorderAdvanced(props.border);

        this.state = {
            border: props.border,
            side  : advanced ? "top" : "all",
            advanced,
        };
    }

    public onTypeChange = (value: number): void => {
        const advanced = !!value;

        this.setState(prevState => {
            const newState = {
                ...prevState,
                side: (advanced ? "top" : "all") as BorderSideType,
                advanced,
            };

            // Check to see if the borders must be set
            if(!advanced) {
                const value = prevState.border[(prevState.side === "all" ? "top" : prevState.side)] as BorderValues;

                newState.border = {
                    top   : value,
                    left  : value,
                    right : value,
                    bottom: value,
                };
            }

            return newState;
        });
    };

    public onChange = (name: string, value: any): void => {
        const { side } = this.state;

        if(side === "all") {
            this.setState(prevState => ({
                border: {
                    top: {
                        ...prevState.border.top,
                        [name]: value,
                    },
                    left: {
                        ...prevState.border.left,
                        [name]: value,
                    },
                    right: {
                        ...prevState.border.right,
                        [name]: value,
                    },
                    bottom: {
                        ...prevState.border.bottom,
                        [name]: value,
                    },
                },
            }));
        } else {
            this.setState(prevState => ({
                border: {
                    ...prevState.border,
                    [side]: {
                        ...prevState.border[side],
                        [name]: value,
                    },
                },
            }));
        }
    };

    public onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.onChange(name, value);
    };

    public onSelectChange = (option: { label: string; value: string; }): void => {
        this.onChange("borderStyle", option.value);
    };

    public onCancel = (): void => {
        this.props.dispatch(hideDialog());
    };

    public onUpdate = (): void => {
        this.props.dispatch(hideDialog());
        this.props.onConfirm(this.state.border);
    };

    public renderSideSettings = (): React.ReactNode => {
        const side = (this.state.side === "all" ? "top" : this.state.side);
        const values = this.state.border[side];

        const value = BORDER_STYLE_OPTIONS.find(option => option.value === values.borderStyle) ?? null;

        return (
            <div className="border-settings-options">
                <label htmlFor={`${side}-width`}>
                    Width
                </label>
                <label htmlFor={`${side}-style`}>
                    Style
                </label>
                <label htmlFor={`${side}-color`}>
                    Colour
                </label>
                <input
                    id={`${side}-width`}
                    className="border-width"
                    type="number"
                    name="borderWidth"
                    value={values.borderWidth}
                    onChange={this.onInputChange}
                />
                <Select
                    id={`${side}-style`}
                    options={BORDER_STYLE_OPTIONS}
                    value={value}
                    className="border-settings-select"
                    onChange={this.onSelectChange}
                />
                <ColorPicker
                    id={`${side}-color`}
                    className="border-color"
                    value={values.borderColor}
                    onChange={(color: string): void => {
                        this.onChange("borderColor", color);
                    }}
                />
            </div>
        );
    };

    public onSideChange = (side: BorderSideType): void => {
        if(!this.state.advanced) {
            return;
        }

        this.setState({
            side,
        });
    };

    public renderSideSelector = (side: BorderSideType): React.ReactNode => {
        const selected = (this.state.side === "all" ? true : this.state.side === side);

        const classes = [ "border-settings-display-selector", side ];

        let Icon = FontAwesomeLight;

        if(selected) {
            Icon = FontAwesomeSolid;
            classes.push("selected");
        }

        if(this.state.side === "all") {
            classes.push("all");
        }

        return (
            <div
                className={classes.join(" ")}
                onClick={(): void => {
                    this.onSideChange(side);
                }}
            >
                <Icon icon={BORDER_SELECTOR_MAP[side]} />
            </div>
        );
    };

    public render(): React.ReactNode {
        return (
            <div className="border-settings-dialog">
                <InlineSelect
                    options={[ "Simple", "Advanced" ]}
                    selected={(this.state.advanced ? 1 : 0)}
                    onSelect={this.onTypeChange}
                />
                <div className="border-settings-display-wrapper">
                    <div
                        className="border-settings-display"
                        style={createBorderStyles(this.state.border)}
                    />
                    {this.renderSideSelector("top")}
                    {this.renderSideSelector("left")}
                    {this.renderSideSelector("right")}
                    {this.renderSideSelector("bottom")}
                </div>
                {this.renderSideSettings()}
                <div className="buttons">
                    <Button
                        classes={[ "button-cancel" ]}
                        onClick={this.onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        classes={[ "button-save" ]}
                        onClick={this.onUpdate}
                    >
                        Update
                    </Button>
                </div>
            </div>
        );
    }
}

export default connector(BorderSettingsDialog);
